// src/components/CheckInModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import {
  StarIcon,
  TagIcon,
  TicketIcon,
  MusicalNoteIcon,
  UserGroupIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";

// Важно для a11y
Modal.setAppElement("#root");

const CLOSE_THRESHOLD = 120; // сколько пикселей протянуть вниз, чтобы закрыть

const CheckInModal = ({ isOpen, onClose, onConfirm, guest, checkedInCounts }) => {
  const guestId = guest?.id;
  const guestPlus = typeof guest?.plus === "number" ? guest.plus : 0;
  const checkedInSoFar = guestId ? checkedInCounts[guestId] || 0 : 0;
  const maxAllowedPeople = Math.max(0, guestPlus + 1 - checkedInSoFar);

  const [numberOfPeople, setNumberOfPeople] = useState(1);

  // === swipe-sheet state ===
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startYRef = useRef(0);
  const sheetRef = useRef(null);

  // Сброс значений при открытии/смене гостя
  useEffect(() => {
    setNumberOfPeople(maxAllowedPeople > 0 ? 1 : 1);
    setDragY(0);
    setIsDragging(false);
  }, [guestId, maxAllowedPeople, isOpen]);

  // Блокируем прокрутку body, пока модалка открыта
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const dec = () => setNumberOfPeople((n) => Math.max(1, n - 1));
  const inc = () => setNumberOfPeople((n) => Math.min(maxAllowedPeople || 1, n + 1));

  const handleConfirm = () => {
    onConfirm(numberOfPeople);
    onClose();
  };

  // ===== Category Badge =====
  const CategoryBadge = useMemo(() => {
    const category = guest?.category || "REGULAR";
    let bg = "bg-gray-100",
      fg = "text-gray-800",
      Icon = TicketIcon;

    switch (category) {
      case "VIP":
        bg = "bg-yellow-100";
        fg = "text-yellow-800";
        Icon = StarIcon;
        break;
      case "BACKSTAGE":
        bg = "bg-purple-100";
        fg = "text-purple-800";
        Icon = TagIcon;
        break;
      case "ARTIST":
        bg = "bg-purple-100";
        fg = "text-purple-800";
        Icon = MusicalNoteIcon;
        break;
      case "STAFF":
        bg = "bg-blue-100";
        fg = "text-blue-800";
        Icon = UserGroupIcon;
        break;
      default:
        Icon = TicketIcon;
    }

    return (
      <span
        className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bg} ${fg}`}
      >
        <Icon className="h-3 w-3 mr-1" />
        {category}
      </span>
    );
  }, [guest?.category]);

  // Быстрые пресеты
  const quickPresets = useMemo(() => {
    const max = Math.min(15, Math.max(1, maxAllowedPeople));
    return Array.from({ length: max }, (_, i) => i + 1);
  }, [maxAllowedPeople]);

  // ===== swipe handlers (тянем ВЕСЬ лист вниз) =====
  const lockInnerScroll = () => {
    if (sheetRef.current) sheetRef.current.style.overflow = "hidden";
  };
  const unlockInnerScroll = () => {
    if (sheetRef.current) sheetRef.current.style.overflow = "";
  };

  const onTouchStart = (e) => {
    if (window.innerWidth >= 640) return; // только мобилка
    const tag = e.target && e.target.tagName ? e.target.tagName.toUpperCase() : "";
    if (/INPUT|BUTTON|SELECT|TEXTAREA/.test(tag)) return; // не ломаем ввод
    setIsDragging(true);
    startYRef.current = e.touches[0].clientY - dragY;
    lockInnerScroll();
  };

  const onTouchMove = (e) => {
    if (!isDragging) return;
    const y = e.touches[0].clientY - startYRef.current;
    const next = Math.max(0, y); // тянем только вниз
    setDragY(next);
    e.preventDefault(); // чтобы текст внутри не скроллился
  };

  const onTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    unlockInnerScroll();
    if (dragY > CLOSE_THRESHOLD) {
      onClose();
    } else {
      setDragY(0); // откат назад
    }
  };

  return (
    <Modal
      isOpen={!!isOpen}
      onRequestClose={() => {
        unlockInnerScroll();
        onClose();
      }}
      shouldCloseOnOverlayClick={true}
      contentLabel="Check-in Confirmation"
      overlayClassName="
        fixed inset-0 bg-black/50
        flex items-end sm:items-center justify-center
        z-[1050]
      "
      // Делаем контейнер прозрачным — сам лист ниже со своим фоном
      className="
        outline-none bg-transparent
        w-full sm:w-auto sm:max-w-lg
      "
    >
      {/* === Сам «лист» (sheet), который двигаем translateY) === */}
      <div
        ref={sheetRef}
        style={{
          transform: `translate3d(0, ${dragY}px, 0)`,
          transition: isDragging ? "none" : "transform 200ms ease",
          willChange: "transform",
          touchAction: "none",
          overscrollBehavior: "contain",
        }}
        className="
          w-full sm:w-auto bg-white
          rounded-t-2xl sm:rounded-xl
          shadow-xl
          p-4 sm:p-6
          max-h-[90vh] overflow-y-auto
        "
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Ручка для свайпа (подсказывает, что можно тянуть) */}
        <div className="sm:hidden mx-auto mb-2 h-1.5 w-10 rounded-full bg-gray-300" />

        {/* Кнопка закрытия */}
        <button
          onClick={() => {
            unlockInnerScroll();
            onClose();
          }}
          aria-label="Close"
          className="absolute right-3 top-3 p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6 text-gray-900">
          Check-in Confirmation for Guest
        </h2>

        <p className="text-gray-700 mb-3 sm:mb-4 leading-relaxed">
          You are about to check in guest:{" "}
          <span className="font-semibold">{guest?.name || "—"}</span>
          {CategoryBadge}
        </p>

        <div className="text-sm text-gray-600 mb-3">
          Remaining:&nbsp;
          <span className="font-semibold">{maxAllowedPeople}</span>
          &nbsp;of&nbsp;
          <span className="font-semibold">{guestPlus + 1}</span>
          &nbsp;total (used: {checkedInSoFar})
        </div>

        {maxAllowedPeople > 0 ? (
          <div className="mb-5 sm:mb-6">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Number of people (including this guest)
            </label>

            {/* Быстрые кнопки */}
            <div className="flex flex-wrap gap-2 mb-3">
              {quickPresets.map((n) => (
                <button
                  key={n}
                  onClick={() => setNumberOfPeople(n)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition
                  ${
                    numberOfPeople === n
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>

            {/* Степпер + инпут */}
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={dec}
                disabled={numberOfPeople <= 1 || isDragging}
                className="h-12 w-12 rounded-lg bg-gray-100 text-gray-800 text-2xl leading-none
                           hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Decrease"
              >
                –
              </button>

              <input
                type="number"
                min={1}
                max={maxAllowedPeople}
                value={numberOfPeople}
                onChange={(e) => {
                  const v = Number(e.target.value || 1);
                  setNumberOfPeople(Math.max(1, Math.min(maxAllowedPeople, v)));
                }}
                className="w-20 h-12 text-center text-lg font-semibold border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                inputMode="numeric"
                pattern="[0-9]*"
              />

              <button
                onClick={inc}
                disabled={numberOfPeople >= maxAllowedPeople || isDragging}
                className="h-12 w-12 rounded-lg bg-gray-100 text-gray-800 text-2xl leading-none
                           hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Increase"
              >
                +
              </button>
            </div>
          </div>
        ) : (
          <div className="mb-5 sm:mb-6">
            <div className="rounded-md bg-yellow-50 p-3 text-sm text-yellow-800">
              No remaining check-ins available for this guest.
            </div>
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-center mt-4">
          <button
            onClick={() => {
              unlockInnerScroll();
              onClose();
            }}
            disabled={isDragging}
            className="h-12 rounded-md border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50"
            type="button"
          >
            Cancel
          </button>

          <button
            onClick={handleConfirm}
            disabled={maxAllowedPeople <= 0 || isDragging}
            className="h-12 rounded-md bg-indigo-600 text-white font-semibold hover:bg-indigo-700
                       disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
          >
            Confirm Check-in
          </button>
        </div>
      </div>
    </Modal>
  );
};

CheckInModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  guest: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    plus: PropTypes.number.isRequired,
    category: PropTypes.string,
  }),
  checkedInCounts: PropTypes.object.isRequired,
};

export default CheckInModal;
